/**
 * A simple event dispatcher, that uses event types as identifiers.
 *
 * To use create a new interface which extends BaseEvent and ideally
 * a helper function to construct the event example:
 *
 * ```ts
 * interface MyEvent extends BaseEvent<'my-event'> {
 *   payload: { myValue: number };
 * }
 *
 * dispatcher.connect<MyEvent>('my-event', (event) => {
 *   console.log('Received my-event with myValue:', event.payload.myValue);
 * });
 *
 * dispatcher.dispatch<MyEvent>({ type: 'my-event', payload: { myValue: 23 } });
 * ```
 */

/**
 * Base event interface, all events must extend this interface.
 */
export interface BaseEvent<T extends string> {
  type: T;
  payload: Record<string, unknown>;
}

/**
 * The event listener type is a generic function that takes an event
 */
type EventListener<T extends BaseEvent<string>> = (event: T) => void;

/**
 * The dispatcher itself
 */
export class Dispatcher {
  private eventListeners: Map<string, Set<EventListener<BaseEvent<string>>>>;

  constructor() {
    this.eventListeners = new Map();
  }

  connect<T extends BaseEvent<string>>(eventType: T['type'], listener: EventListener<T>): void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, new Set());
    }
    this.eventListeners.get(eventType)!.add(listener as EventListener<BaseEvent<string>>);
  }

  disconnect<T extends BaseEvent<string>>(eventType: T['type'], listener: EventListener<T>): void {
    if (this.eventListeners.has(eventType)) {
      const listeners = this.eventListeners.get(eventType)!;
      listeners.delete(listener as EventListener<BaseEvent<string>>);
      if (listeners.size === 0) {
        this.eventListeners.delete(eventType);
      }
    }
  }

  dispatch<T extends BaseEvent<string>>(event: T): void {
    if (this.eventListeners.has(event.type)) {
      const listeners = this.eventListeners.get(event.type)!;
      for (const listener of listeners) {
        (listener as EventListener<T>)(event);
      }
    }
  }
}
